// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledOverlay from "@sc/App/Components/Common/StyledOverlay";
import XngUtilities from "@sc/App/Components/Common/XngUtilities";
import XngZone from "@sc/App/Components/Common/XngZone";
import { xHeaderUpdate } from "@sc/App/Handlers/XNG/Header";
import useSize from "@sc/App/Hooks/useSize";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { getLogoImage, getLogoImageWidget, xIsAuthenticated } from "@sc/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@sc/XDL/constants";
import { parseBoolean } from "@sc/XDL/Utilities";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    // XNG-Changes
    currentLocation: getLocation(state),
    isSticky: state.xHeader.isSticky,
    isCheckoutPage:
        checkoutPages.includes(getCurrentPage(state).type) ||
        getPageLinkByPageType(state, "CheckoutShippingPage")?.url === getReturnUrl(state),
    logoImage: getLogoImage(state),
    navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
    isAuthenticated: xIsAuthenticated(state),
    currentShipToState: getCurrentShipToState(state),
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    logoImageWidget: getLogoImageWidget(state),
    isHomepage: getCurrentPage(state).type === "HomePage",
    collectCustomerAddress: parseBoolean(getSession(state)?.properties?.collectCustomAddress),
});

const mapDispatchToProps = {
    xHeaderUpdate,
    loadCurrentShipTo,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerWrapper?: InjectableCss;
    // XNG-Change
    printLogo?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
    checkoutHeaderWrapper?: InjectableCss;
    signInCartlinks?: InjectableCss;
    secondaryNavWrapper?: HiddenProps;
    headerLinkList?: InjectableCss;
    logo?: InjectableCss;
    searchWrapper?: InjectableCss;
    checkoutHelpMsg?: InjectableCss;
    checkoutLogo?: InjectableCss;
    alertWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    currentLocation,
    isSticky,
    isCheckoutPage,
    xHeaderUpdate,
    logoImage,
    navDrawerIsOpen,
    isAuthenticated,
    currentShipToState,
    loadCurrentShipTo,
    shellContext,
    isSignInPage,
    logoImageWidget,
    // XNG-Change
    isHomepage,
    collectCustomerAddress,
}: Props) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;

    // Hide main navigation
    useEffect(() => {
        setTimeout(() => hideAll());
        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        }
    }, [isSticky, currentLocation]);

    // XNG-Changes
    useEffect(() => {
        if (headerHeight) {
            // include border-bottom
            xHeaderUpdate({ key: "headerHeight", value: `${headerHeight + 1}px` });
        }
    }, [headerHeight]);

    // XNG-Change
    useEffect(() => {
        if (isAuthenticated && !currentShipToState.value && !currentShipToState.isLoading) {
            loadCurrentShipTo();
        }
    }, []);

    if (isSignInPage) {
        return null;
    }

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImage && (
                <StyledWrapper {...styles.printLogo}>
                    <img alt={translate("Home")} src={logoImage} {...styles.printLogo} />
                </StyledWrapper>
            )}
            <StyledWrapper ref={headerRef} {...styles.headerWrapper} {...{ isAuthenticated }}>
                {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
                <Page as="header" background="common.ui1" isHeader={true} {...styles.page} {...{ isCheckoutPage }}>
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <EagerLoadingLogoProvider value={true}>
                        {!isCheckoutPage && (
                            <>
                                <Hidden below="lg" {...styles.secondaryNavWrapper}>
                                    <XngZone
                                        contentId={id}
                                        zoneName="SecondaryNavigation"
                                        hint="Secondary Navigation"
                                    />
                                </Hidden>
                                <StyledWrapper {...styles.container} {...{ isAuthenticated }} {...{ isCheckoutPage }}>
                                    <Hidden above="md">
                                        <XngZone contentId={id} zoneName="MainNavigation" hint="MainNavigation" />
                                    </Hidden>
                                    <StyledWrapper {...styles.logo}>
                                        <XngZone contentId={id} zoneName="Logo" hint="Logo" />
                                    </StyledWrapper>
                                    {(shellContext.isEditing || isAuthenticated) && (
                                        <StyledWrapper {...styles.searchWrapper}>
                                            <XngZone contentId={id} zoneName="Search" hint="Search" />
                                        </StyledWrapper>
                                    )}
                                    {(shellContext.isEditing || isAuthenticated) && (
                                        <StyledWrapper {...styles.signInCartlinks}>
                                            <XngZone contentId={id} zoneName="HeaderSignIn" hint="Header sign in" />
                                            <XngZone contentId={id} zoneName="CartLink" hint="Cart link" />
                                        </StyledWrapper>
                                    )}
                                    {(shellContext.isEditing || !isAuthenticated) && (
                                        <StyledWrapper {...styles.headerLinkList}>
                                            <XngZone contentId={id} zoneName="HeaderLinkList" hint="Header link list" />
                                            <XngZone contentId={id} zoneName="ContactNumber" hint="Contact Number" />
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                <Hidden below="lg">
                                    <StyledWrapper {...styles.mainNavigationWrapper}>
                                        <XngZone contentId={id} zoneName="MainNavigation" hint="MainNavigation" />
                                    </StyledWrapper>
                                </Hidden>
                                {((isHomepage && collectCustomerAddress) || shellContext.isEditing) && (
                                    <XngZone contentId={id} zoneName="EmptyCartAlert" hint="EmptyCartAlert" />
                                )}
                            </>
                        )}
                        {isCheckoutPage && (
                            <Page {...styles.checkoutHeaderWrapper}>
                                <StyledWrapper {...styles.checkoutLogo}>
                                    {createWidgetElement("Basic/Logo", {
                                        fields: {
                                            mobileSpecificImage: logoImageWidget?.logoImage,
                                            logoImage: logoImageWidget?.logoImage,
                                        },
                                    })}
                                </StyledWrapper>
                                <StyledWrapper {...styles.checkoutHelpMsg}>
                                    {siteMessage("Custom_Header_Need_Help_Msg")}
                                </StyledWrapper>
                            </Page>
                        )}
                    </EagerLoadingLogoProvider>
                    <span ref={afterHeader} tabIndex={-1} />
                </Page>
                {/* <XNG-Changes> */}
                <XngUtilities />
                {/* </XNG-Changes> */}
            </StyledWrapper>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(Header)),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
